import type { CameraDetail, CameraMonitorItem } from "../../../src/contracts/admin";
import { Camera, ImageOff, LoaderCircle, Pencil, Power, Route } from "lucide-react";
import { CameraFramePreview } from "./CameraFramePreview";
import { cameraMonitorStatePresentation } from "../lib/camera-monitor";
import { formatRelative } from "../lib/format";
import { Button, StatusBadge } from "./ui";

export function CameraCard({
  camera,
  onEdit,
  onToggle,
  onDestination,
  monitor,
  previewStatus,
  onPreview,
}: {
  camera: CameraDetail;
  onEdit: () => void;
  onToggle: () => void;
  onDestination: () => void;
  monitor?: CameraMonitorItem | undefined;
  previewStatus?: "loading" | "error" | undefined;
  onPreview?: ((camera: CameraMonitorItem) => void) | undefined;
}) {
  const monitorPresentation = monitor ? cameraMonitorStatePresentation(monitor.monitor_state) : null;
  const tone = monitorPresentation?.tone ?? (!camera.enabled
    ? "neutral"
    : camera.situation === "ok"
      ? "success"
      : camera.situation === "preta"
        ? "warning"
        : camera.situation === "erro"
          ? "danger"
          : "neutral");
  const label = monitorPresentation?.label ?? (!camera.enabled
    ? "Desabilitada"
    : camera.situation === "preta"
      ? "Frame preto"
      : camera.situation === "erro"
        ? "Com erro"
        : camera.situation === "ok"
          ? "Saudável"
          : "Sem diagnóstico");

  return (
    <article className="camera-card">
      {monitor && onPreview ? <CameraFramePreview camera={monitor} compact onOpen={onPreview} /> : previewStatus ? (
        <div className="camera-frame camera-frame--compact"><div className="camera-frame__empty">{previewStatus === "loading" ? <LoaderCircle className="spin" size={20} /> : <ImageOff size={20} />}<span>{previewStatus === "loading" ? "Carregando frame" : "Frame indisponível"}</span></div></div>
      ) : null}
      <header>
        <span className="camera-card__icon"><Camera size={19} /></span>
        <div><h3>{camera.display_name}</h3><small>{camera.source.ip} · canal {camera.source.channel}</small></div>
        <StatusBadge tone={tone}>{label}</StatusBadge>
      </header>
      <div className="camera-card__stats">
        <span><small>Última captura</small><strong>{formatRelative(camera.last_capture_at)}</strong></span>
        <span><small>OK 24h</small><strong>{camera.ok_24h ?? 0}</strong></span>
        <span><small>Pretas</small><strong>{camera.black_24h ?? 0}</strong></span>
        <span><small>Erros</small><strong>{camera.errors_24h ?? 0}</strong></span>
      </div>
      <div className="camera-card__flags">
        <span>{camera.enabled ? "Capturas ativas" : "Capturas pausadas"}</span>
        <span>{camera.upload_enabled ? "Uploads ativos" : "Uploads pausados"}</span>
      </div>
      {camera.detail ? <p className="camera-card__detail">{camera.detail}</p> : null}
      <div className="camera-card__path"><Route size={15} /><span title={camera.key_prefix ?? ""}>{camera.key_prefix ?? "Sem destino ativo"}</span></div>
      <footer>
        <Button aria-label={`Editar ${camera.display_name}`} variant="ghost" onClick={onEdit}><Pencil size={15} /> Editar</Button>
        <Button aria-label={`Alterar destino de ${camera.display_name}`} variant="ghost" onClick={onDestination}><Route size={15} /> Destino</Button>
        <Button aria-label={`${camera.enabled ? "Desativar" : "Reativar"} ${camera.display_name}`} variant="ghost" onClick={onToggle}><Power size={15} /> {camera.enabled ? "Desativar" : "Reativar"}</Button>
      </footer>
    </article>
  );
}
